// lib/api.ts — typed client for the Self Gateway (spec §2.3, §8).
// The browser only ever talks to the gateway; fetch + token getter are injectable
// so the client is fully unit-testable. NEVER handles secrets beyond passing the JWT.

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export type TokenGetter = () => Promise<string | null>;

export interface ApiClientOptions {
  gatewayUrl: string;
  getToken: TokenGetter;
  fetchFn?: typeof fetch;
}

// ---- Response shapes (mirror the backend) ----
export interface SetupStatus {
  key1Configured: boolean;
  key1Provider: string | null;
  model: string | null;
  reasoning: string | null;
  contextWindow: number | null;
  voiceRecordingEnabled: boolean;
  liveVoiceEnabled: boolean;
  theme: string;
}
export interface ModelInfo {
  id: string;
  label: string;
}
export interface CopilotOauthStart {
  deviceCode: string;
  userCode: string;
  verificationUri: string;
  interval: number;
  expiresIn: number;
}
export interface ModelCapabilities {
  reasoningLevels: string[];
  contextWindows: number[];
  multimodalAudio: boolean;
  source: string;
}
export interface RecallHit {
  id: string;
  source: string;
  unit_type: string;
  content_distilled: string;
  context: Record<string, unknown>;
  extracted: Record<string, unknown>;
  similarity: number;
}
export interface Settings {
  theme: string;
  key1Provider: string | null;
  model: string | null;
  reasoning: string | null;
  contextWindow: number | null;
  key2Provider: string | null;
  key2Endpoint: string | null;
  key2Model: string | null;
  voiceRecordingEnabled: boolean;
  liveVoiceEnabled: boolean;
}
export interface ChatSession {
  id: string;
  title: string | null;
  modality: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}
export interface ChatMessage {
  id: string;
  role: 'owner' | 'agent';
  content: string;
  createdAt: string;
}
export interface VoiceTurn {
  role: 'user' | 'assistant';
  text: string;
}
export interface VoiceCall {
  id: string;
  startedAt: string;
  endedAt: string;
  durationSeconds: number;
  turnCount: number;
  transcript: VoiceTurn[];
  createdAt: string;
}
export interface Source {
  id: string;
  kind: string;
  label: string | null;
  ref: string | null;
  meta: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}
export interface AgentTokenInfo {
  id: string;
  name: string;
  tokenPrefix: string;
  scopes: string[];
  unitTypes: string[];
  rateLimitPerMin: number;
  revokedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
}

export class ApiClient {
  private readonly fetchFn: typeof fetch;
  constructor(private readonly opts: ApiClientOptions) {
    this.fetchFn = opts.fetchFn ?? fetch.bind(globalThis);
  }

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const token = await this.opts.getToken();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;
    const res = await this.fetchFn(`${this.opts.gatewayUrl}/gateway${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await res.text();
    const data = text ? JSON.parse(text) : {};
    if (!res.ok) {
      const err = (data as { error?: { code?: string; message?: string } }).error;
      throw new ApiError(res.status, err?.code ?? 'error', err?.message ?? `HTTP ${res.status}`);
    }
    return data as T;
  }

  // ---- Setup / provider config ----
  getSetupStatus = () => this.request<SetupStatus>('GET', '/setup/status');
  setKey1 = (body: { provider: string; apiKey?: string; oauthToken?: string }) =>
    this.request<{ ok: true }>('POST', '/setup/key1', body);
  startCopilotOauth = () => this.request<CopilotOauthStart>('POST', '/setup/key1/oauth/start');
  pollCopilotOauth = (deviceCode: string) =>
    this.request<{ status: 'pending' | 'ok'; error?: string }>('POST', '/setup/key1/oauth/poll', {
      deviceCode,
    });
  listModels = () => this.request<{ models: ModelInfo[] }>('GET', '/setup/models');
  getModelCapabilities = (modelId: string) =>
    this.request<ModelCapabilities>(
      'GET',
      `/setup/models/${encodeURIComponent(modelId)}/capabilities`,
    );
  selectKey1 = (body: { model: string; reasoning: string; contextWindow: number }) =>
    this.request<{ ok: true; voiceRecordingEnabled: boolean }>('POST', '/setup/key1/select', body);
  setKey2 = (body: { provider: string; apiKey: string; endpoint?: string; model?: string }) =>
    this.request<{ ok: true; liveVoiceEnabled: true }>('POST', '/setup/key2', body);
  getSettings = () => this.request<Settings>('GET', '/settings');
  patchSettings = (body: { theme: string }) => this.request<Settings>('PATCH', '/settings', body);

  // ---- Ingestion ----
  ingestDoc = (body: {
    fileName: string;
    mimeType: string;
    contentBase64: string;
    text?: string;
  }) => this.request<{ eventId: string; corpusId: string }>('POST', '/ingest/doc', body);
  createUploadUrl = (body: { fileName: string }) =>
    this.request<{ path: string; uploadUrl: string; maxBytes: number }>(
      'POST',
      '/ingest/upload-url',
      body,
    );
  processDocUpload = (body: { path: string; fileName: string; mimeType: string }) =>
    this.request<{ eventId: string; corpusId: string }>('POST', '/ingest/doc/process', body);
  /** PUT a file straight to a Supabase signed upload URL (large/zip uploads). */
  putToSignedUrl = async (url: string, file: Blob, contentType: string): Promise<void> => {
    const res = await this.fetchFn(url, {
      method: 'PUT',
      headers: { 'content-type': contentType, 'x-upsert': 'true' },
      body: file,
    });
    if (!res.ok) {
      throw new ApiError(res.status, 'storage_upload_failed', `Upload failed (HTTP ${res.status})`);
    }
  };
  ingestNote = (body: { text: string; title?: string }) =>
    this.request<{ eventId: string }>('POST', '/ingest/note', body);
  ingestGit = (body: {
    provider: string;
    repoUrl: string;
    token: string;
    authorIdentity?: string;
  }) => this.request<{ eventId: string }>('POST', '/ingest/git', body);
  startVoiceSession = () => this.request<{ sessionId: string }>('POST', '/voice/session');
  voiceTurn = (body: { sessionId: string; mimeType: string; audioBase64: string }) =>
    this.request<{ eventId: string }>('POST', '/voice/turn', body);
  startRealtimeSession = () =>
    this.request<{
      ephemeralKey: string;
      webrtcUrl: string;
      model: string;
      expiresAt: number | null;
    }>('POST', '/voice/realtime/session');
  saveCallTranscript = (body: { transcript: VoiceTurn[]; startedAt?: string; model?: string }) =>
    this.request<{ callId: string; turns: number }>('POST', '/voice/call', body);
  listVoiceCalls = () => this.request<{ calls: VoiceCall[] }>('GET', '/voice/calls');
  rememberFact = (body: { content: string }) =>
    this.request<{ ok: true }>('POST', '/memory/remember', body);
  toolSearch = (body: { query: string }) =>
    this.request<{ results: { title: string; url: string }[] }>('POST', '/tools/search', body);
  toolFetch = (body: { url: string }) =>
    this.request<{ text: string }>('POST', '/tools/fetch', body);

  // ---- Sources (docs/notes/git management) ----
  listSources = (kind: string) =>
    this.request<{ sources: Source[] }>('GET', `/sources?kind=${encodeURIComponent(kind)}`);
  deleteSource = (id: string) =>
    this.request<{ ok: true }>('DELETE', `/sources/${encodeURIComponent(id)}`);
  updateNote = (id: string, body: { text: string; title?: string }) =>
    this.request<{ ok: true; eventId: string }>('PATCH', `/notes/${encodeURIComponent(id)}`, body);

  // ---- Agent tokens (per-agent identity) ----
  listAgents = () => this.request<{ agents: AgentTokenInfo[] }>('GET', '/agents');
  mintAgent = (body: {
    name: string;
    scopes: string[];
    unitTypes: string[];
    rateLimitPerMin: number;
  }) => this.request<{ id: string; token: string; tokenPrefix: string }>('POST', '/agents', body);
  revokeAgent = (id: string) =>
    this.request<{ ok: true }>('DELETE', `/agents/${encodeURIComponent(id)}`);

  // ---- Chat ----
  startChatSession = (body: { modality: string }) =>
    this.request<{ sessionId: string; modality: string }>('POST', '/chat/session', body);
  listChatSessions = () => this.request<{ sessions: ChatSession[] }>('GET', '/chat/sessions');
  chatInit = (opts: { limit?: number } = {}) =>
    this.request<{ sessionId: string; messages: ChatMessage[]; hasMore: boolean }>(
      'GET',
      `/chat/init${opts.limit ? `?limit=${opts.limit}` : ''}`,
    );
  listChatMessages = (sessionId: string, opts: { limit?: number; before?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.before) q.set('before', opts.before);
    const qs = q.toString();
    return this.request<{ messages: ChatMessage[]; hasMore: boolean }>(
      'GET',
      `/chat/session/${encodeURIComponent(sessionId)}/messages${qs ? `?${qs}` : ''}`,
    );
  };
  chatMessage = (body: { sessionId: string; content: string }) =>
    this.request<{ reply: string; captured?: boolean }>('POST', '/chat/message', body);
  chatVoiceTurn = (body: { sessionId: string; transcript: string }) =>
    this.request<{ reply: string; transcript: string }>('POST', '/chat/voice/turn', body);
  closeChatSession = (body: { sessionId: string }) =>
    this.request<{ ok: true }>('POST', '/chat/session/close', body);

  // ---- Retrieval ----
  recall = (body: { query: string; source?: string; unitType?: string }) =>
    this.request<{ hits: RecallHit[] }>('POST', '/recall', body);
  getPersona = () => this.request<Record<string, unknown>>('GET', '/persona');
  myPatterns = () => this.request<{ patterns: RecallHit[] }>('GET', '/my-patterns');
  myPrinciples = () => this.request<{ principles: RecallHit[] }>('GET', '/my-principles');

  // ---- Owner control ----
  exportData = (scope: string) =>
    this.request<Record<string, unknown>>('GET', `/export?scope=${encodeURIComponent(scope)}`);
  deletePersona = () => this.request<{ ok: true }>('DELETE', '/persona');
  deleteAll = () => this.request<{ ok: true }>('DELETE', '/all');
  getAudit = () => this.request<{ entries: Record<string, unknown>[] }>('GET', '/audit');
}
